import fp from 'lodash/fp'
import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import { assertUnreachableCode } from 'common/src/vtj/typescript.util'
import { getValtioKoodistoEntryByCode } from 'todistukset-common/src/vtj/country.util'
import { type CountryKoodistoCode } from 'todistukset-common/src/vtj/vtj-koodisto.util'
import CertificateTypes from 'todistukset-common/src/vtj/CertificateTypes.enum'
import { ReligiousCommunity } from 'todistukset-common/src/vtj/vtj-kysely/aetpohja-soap-response-converter'
import {
  UskonnollinenYhdyskunta,
  editVtjArvo,
  finalEditableFieldValue,
  Kansalaisuus,
  mapEditableField,
  EditableField,
  Kayttoalue,
  FlattenedEditableField,
} from 'todistukset-common/src/vtj/types/editable-field.type'
import { getCodeFromUsageRegion } from 'todistukset-common/src/vtj/usage-region.util'

export const getTodistusTypeTranslationKey = (
  todistusType: CertificateTypes
): string => {
  switch (todistusType) {
    case CertificateTypes.ASUINPAIKKATODISTUS:
      return 'homeLocationCert'
    case CertificateTypes.ELOSSAOLOTODISTUS:
      return 'aliveCertificate'
    case CertificateTypes.AVIOLIITTOTODISTUS:
      return 'marriageCert'
    case CertificateTypes.SIVIILISAATYTODISTUS:
      return 'maritalStatusCert'
    case CertificateTypes.SYNTYMATODISTUS:
      return 'birthCert'
    case CertificateTypes.AETTODISTUS:
      return 'aetCert'
    case CertificateTypes.UAETTODISTUS:
      return 'uaetCert'
    case CertificateTypes.TODISTUSASUMISTIEDOILLA:
      return 'residenceCert'
    case CertificateTypes.TODISTUSHENKILOJAPERHESUHDETIEDOILLA:
      return 'personAndFamilyRelationshipCert'
    case CertificateTypes.TODISTUSKUOLEMASTA:
      return 'deathCert'
    default:
      assertUnreachableCode(
        todistusType,
        `${todistusType} is not valid todistus type.`
      )
  }
}

export const convertReligiousCommunitiesIntoUskonnollinenYhdyskunta = (
  religiousCommunities: ReligiousCommunity[] | null,
  lang: LanguageCode
): UskonnollinenYhdyskunta[] => {
  if (religiousCommunities === null) {
    return []
  }

  return religiousCommunities.map((religiousCommunity) => {
    if (
      (lang === LanguageCode.FI || lang === LanguageCode.SV) &&
      religiousCommunity[lang]
    ) {
      return editVtjArvo<UskonnollinenYhdyskunta>(
        null,
        religiousCommunity[lang]
      )
    }

    return editVtjArvo<UskonnollinenYhdyskunta>(
      null,
      religiousCommunity[LanguageCode.FI] ?? religiousCommunity[LanguageCode.SV]
    )
  })
}

export const getDisplayValueForReligiousCommunities = (
  religiousCommunities: ReligiousCommunity[] | null,
  lang: LanguageCode
): string => {
  const uskonnollisetYhdyskunnat =
    convertReligiousCommunitiesIntoUskonnollinenYhdyskunta(
      religiousCommunities,
      lang
    )

  return getDisplayValueForUskonnnollinenYhdyskunta(uskonnollisetYhdyskunnat)
}

export const getDisplayValueForUskonnnollinenYhdyskunta = (
  religiousCommunityTranslated: UskonnollinenYhdyskunta[]
): string => {
  return religiousCommunityTranslated
    .map((editableField) => finalEditableFieldValue(editableField))
    .join(', ')
}

export const getDisplayValueForCitizenshipCode = (
  citizenshipCode: Kansalaisuus | null,
  lang: LanguageCode
): EditableField<string> =>
  mapEditableField(citizenshipCode, (editableFieldValue) => {
    const countryNames =
      editableFieldValue?.map((kansalaisuuskoodi) =>
        kansalaisuuskoodi
          ? getDisplayValueForCountryCode(kansalaisuuskoodi, lang)
          : null
      ) ?? []
    return countryNames.length ? countryNames.join(', ') : null
  })

export const getDisplayValueForCountryCode = (
  countryCode: CountryKoodistoCode | null,
  lang: LanguageCode
): string => {
  const koodistoEntry = getValtioKoodistoEntryByCode(countryCode)
  if (
    koodistoEntry &&
    (lang === LanguageCode.FI ||
      lang === LanguageCode.SV ||
      lang === LanguageCode.EN) &&
    !fp.isNil(koodistoEntry[lang])
  ) {
    /**
     * Country names are translated in three languages in vtj-koodisto: fi, sv and en.
     * Return country name with requested language if available.
     */
    return koodistoEntry[lang]
  } else if (!fp.isNil(koodistoEntry)) {
    /**
     * As a fallback. If we have koodisto entry for country code but we did not have translation
     * with requested language then return one of the available translations if available.
     */
    if (
      fp.isNil(koodistoEntry[LanguageCode.FI]) &&
      fp.isNil(koodistoEntry[LanguageCode.SV]) &&
      fp.isNil(koodistoEntry[LanguageCode.EN])
    ) {
      throw new Error(
        `Unable to find translation for country code "${countryCode}. All translations 'fi', 'sv' and 'en' are null."`
      )
    } else {
      return (
        koodistoEntry[LanguageCode.FI] ??
        koodistoEntry[LanguageCode.SV] ??
        koodistoEntry[LanguageCode.EN]
      )
    }
  } else {
    throw new Error(
      `Unable to find koodisto entry for country code "${countryCode}"`
    )
  }
}

export const getDisplayValueForUsageRegion = (
  usageRegion: Kayttoalue | FlattenedEditableField<Kayttoalue> | null,
  lang: LanguageCode
): string | null => {
  const countryKoodistoCode = getCodeFromUsageRegion(usageRegion)
  return countryKoodistoCode
    ? getDisplayValueForCountryCode(countryKoodistoCode, lang)
    : null
}

export const getDisplayValueForKansalaisuus = (
  citizenshipCode: Kansalaisuus | null,
  lang: LanguageCode
): string => {
  const kansalaisuuskoodit = finalEditableFieldValue(citizenshipCode) ?? []

  return kansalaisuuskoodit
    .map((koodi) => {
      return koodi === null ? null : getDisplayValueForCountryCode(koodi, lang)
    })
    .join(', ')
}
